import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PaymentSimulatorService } from '@services/payment-simulator.service';
import { PaymentSimulationRecord } from '@interfaces/';
import { AdminService } from '@services/admin.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';

@Component({
  selector: 'app-payment-simulator-admin',
  templateUrl: './payment-simulator-admin.component.html',
  styleUrls: ['./payment-simulator-admin.component.scss'],
  standalone: false
})
export class PaymentSimulatorAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  transactions: PaymentSimulationRecord[] = [];
  isEnabled: boolean = false;
  statistics: any = null;
  showTransactions: boolean = false;
  
  // Solo mostrar en ambientes NO productivos
  shouldShow: boolean = !environment.production;

  constructor(
    private paymentSimulator: PaymentSimulatorService,
    private translate: TranslateService,
    private adminService: AdminService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.loadData();
    
    // Escuchar cambios en las transacciones
    this.paymentSimulator.getSimulatedTransactions()
      .pipe(takeUntil(this.destroy$))
      .subscribe(transactions => {
        this.transactions = transactions;
        this.statistics = this.paymentSimulator.getStatistics();
      });

    // Actualizar el estado del badge cada 500ms para reflejar cambios desde el switch
    setInterval(() => {
      this.isEnabled = this.adminService.admin?.Payment_And_Weighing_Test && environment.isCert;
    }, 500);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(): void {
    // El estado del badge refleja directamente el switch Payment_And_Weighing_Test
    this.isEnabled = this.adminService.admin?.Payment_And_Weighing_Test && environment.isCert;
    this.transactions = this.paymentSimulator.getSimulatedTransactionsArray();
    this.statistics = this.paymentSimulator.getStatistics();
  }

  clearTransactions(): void {
    Swal.fire({
      title: this.translate.instant('payment_simulator.admin.clear_confirm_title'),
      text: this.translate.instant('payment_simulator.admin.clear_confirm_message'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('payment_simulator.admin.clear_confirm'),
      cancelButtonText: this.translate.instant('cancel'),
      customClass: {
        container: 'am-alert-container',
        popup: 'am-alert-popup',
        confirmButton: 'am-alert-button am-alert-button-cancel',
        cancelButton: 'am-alert-button am-alert-button-confirm'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.paymentSimulator.clearSimulatedTransactions();
        Swal.fire({
          title: this.translate.instant('payment_simulator.admin.cleared_title'),
          icon: 'success',
          confirmButtonText: this.translate.instant('accept'),
          customClass: {
            container: 'am-alert-container',
            popup: 'am-alert-popup',
            confirmButton: 'am-alert-button am-alert-button-confirm'
          }
        });
      }
    });
  }

  deleteTransaction(id: string): void {
    this.paymentSimulator.deleteTransaction(id);
  }

  exportTransactions(): void {
    const json = this.paymentSimulator.exportTransactions();
    const blob = new Blob([json], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-simulations-${new Date().getTime()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    Swal.fire({
      title: this.translate.instant('payment_simulator.admin.exported_title'),
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      customClass: {
        container: 'am-alert-container',
        popup: 'am-alert-popup'
      }
    });
  }

  openTransactionsModal(content: any): void {
    this.modalService.open(content, {
      size: 'xl',
      centered: true,
      scrollable: true,
      windowClass: 'am-modal-content'
    });
  }

  getScenarioClass(scenario: string): string {
    const classes = {
      success: 'badge-success',
      declined: 'badge-danger',
      timeout: 'badge-warning',
      insufficient_funds: 'badge-danger',
      invalid_card: 'badge-danger',
      network_error: 'badge-warning'
    };
    return classes[scenario] || 'badge-secondary';
  }

  getBrandIcon(brand: string): string {
    const icons = {
      VISA: 'cc-visa',
      AMEX: 'cc-amex',
      MC: 'cc-mastercard'
    };
    return icons[brand] || 'credit-card';
  }
}

